
import { useCallback, useMemo, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { PrincipalDocumentosLista } from "../../../auditoria/domain/models/documentos_lista";
import CommonConstants from "../../../../common/constants";
import { DashboardProjetosInfo } from "../../domain/models/dashboard_projetos_info";
import { usePrincipalProjetosDocPagination } from "../hooks/principal_projetos/pagination";
import { useProjetoImpressaoFilters } from "../hooks/Impressao_listasolicitar/filter";
import { useProjetoRevisaoFilters } from "../hooks/Revisao_listasolicitar/filter";

/**
 * usePrincipalProjetosViewModel
 */

type LoadPrincipalListaFuncState = {
    IdArquivoModulo?: number;
    page?: number;
    rowsPerPage?: number;
    tipoPesquisa?: number,
    pesquisa?: string,

};

export const usePrincipalProjetosViewModel = () => {
    const {
        commons: {
            getAllListagemDocUseCase,
            getGedArquivoDataUseCase,
        },

        projetos: {
            getDashboardProjetosInfoUseCase,
        },

    } = useDependencyInjection();

    const backOrNavigate = useBackOrNavigate();
    const pagination = usePrincipalProjetosDocPagination();

    const { pushErrorNotification } = usePageLoader();

    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const [dashboardProjetosInformation, setDashboardProjetosInformation] = useState<DashboardProjetosInfo>();

    const [docLista, setDocLista] = useState<PrincipalDocumentosLista[]>([]);

    const impressaoListaFilters = useProjetoImpressaoFilters();
    const revisaoListaFilters = useProjetoRevisaoFilters();

    const cardIds = useMemo(() => {
        return {
            projetosAtrasados: 1,
            disciplinasAtrasadas: 2,
            subdisciplinasAtrasadas: 3,
            impressoesPendentes: 4,
            impressoesAprovadas: 5,
            impressoesReprovadas: 6,
            revisoesEmAberto: 7,
            revisoesEmAnalise: 8,
            revisoesEncerradas: 9,
        };
    }, [
    ]);

    //#region CARREGAMENTO 

    const loadDocListaState = useCallback(async (funcState?: LoadPrincipalListaFuncState) => {
        try {
            const filteredPage = funcState?.page ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllListagemDocUseCase.getAllListagemDoc(
                String(CommonConstants.moduloDocumentos.Projeto),
                filteredPage,
                filteredRowsPerPage
            );
            setDocLista(getAllListagemDocUseCase.items);
            pagination.setRowsPerPage(getAllListagemDocUseCase.pageSize);
            pagination.setTotalItems(getAllListagemDocUseCase.totalItems);
        } catch (e: unknown) {
            throw new Error("Falha ao buscar documentos!");
        }
    }, [getAllListagemDocUseCase, pagination]);


    const loadDashboardProjetosInfo = useCallback(async (ano?: number) => {
        try {
            setLoading(true);
            const response = await getDashboardProjetosInfoUseCase.execute();
            setDashboardProjetosInformation(response);

        } catch (e: unknown) {
            setDashboardProjetosInformation(undefined);
            throw e;
        } finally {
            setLoading(false);
        }
    }, [
        getDashboardProjetosInfoUseCase,
    ]);



    //#endregion


    /**
     * useEffect
     */

    usePageStartBootstrap(async () => {
        try {
            await Promise.all([
                loadDashboardProjetosInfo(),
                loadDocListaState(),
            ]);

        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao carregar os dados!")[0]);
        }
    });

    /**
     * Eventos
     */

    const handleReload = useCallback(() => {
        try {
            setLoading(true);
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao recarregar os dados!"));
        } finally {
            setLoading(false);
        }
    }, [
        pushErrorNotification,
    ]);


    const handleCardClick = useCallback((cardId: number) => {
        switch (cardId) {
            case cardIds.projetosAtrasados:
                backOrNavigate("/Projetos_lista");
                break;
            case cardIds.disciplinasAtrasadas:
                backOrNavigate("/Projetos_lista");
                break;
            case cardIds.subdisciplinasAtrasadas:
                backOrNavigate("/Projetos_lista");
                break;
            case cardIds.impressoesPendentes:
                impressaoListaFilters.clear();
                impressaoListaFilters.setStatus(0);
                backOrNavigate("/Impressao_listagerenciar");
                break;
            case cardIds.impressoesAprovadas:
                impressaoListaFilters.clear();
                impressaoListaFilters.setStatus(1);
                backOrNavigate("/Impressao_listagerenciar");
                break;
            case cardIds.impressoesReprovadas:
                impressaoListaFilters.clear();
                impressaoListaFilters.setStatus(2);
                backOrNavigate("/Impressao_listagerenciar");
                break;
            case cardIds.revisoesEmAberto:
                revisaoListaFilters.clear();
                revisaoListaFilters.setStatus([0]);
                backOrNavigate("/Revisao_listagerenciar");
                break;
            case cardIds.revisoesEmAnalise:
                revisaoListaFilters.clear();
                revisaoListaFilters.setStatus([1]);
                backOrNavigate("/Revisao_listagerenciar");
                break;
            case cardIds.revisoesEncerradas:
                revisaoListaFilters.clear();
                revisaoListaFilters.setStatus([2]);
                backOrNavigate("/Revisao_listagerenciar");
                break;
        }
    }, [
        cardIds,
        impressaoListaFilters,
        revisaoListaFilters,
        backOrNavigate,
    ]);



    // #region TABELA DOCUMENTOS

    const handleDownloadFile = useCallback(async (IdArquivoModulo: number, NomeArquivo: string) => {
        if (IdArquivoModulo > 0) {
            try {
                setLoading(true);
                await getGedArquivoDataUseCase
                    .getGedArquivoData(IdArquivoModulo, NomeArquivo);
            } catch (e: unknown) {
                pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
            } finally {
                setLoading(false);
            }
        }
    }, [getGedArquivoDataUseCase, pushErrorNotification]);


    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);;
            await loadDocListaState({ page: page + 1, });
        } catch (e: unknown) {
            pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
        } finally {
            setLoading(false);
        }
    }, [loadDocListaState, pagination, pushErrorNotification]);


    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);

            await loadDocListaState({ page: 1, rowsPerPage });
        } catch (e: unknown) {
            pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
        } finally {
            setLoading(false);
        }
    }, [loadDocListaState, pagination, pushErrorNotification]);

    //#endregion


    return {
        loading,
        errorMessage,
        dashboardProjetosInformation: dashboardProjetosInformation ?? null,
        cardIds,
        docLista,
        pagination,

        handleDownloadFile,
        handlePageChange,
        handleRowsPerPageChange,
        handleReload,
        handleCardClick,
    };
}